import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText, Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import { usePatientTreatmentHistoryFiltered } from '@/hooks/useDental';
import type { TreatmentHistoryFilters } from '@/hooks/useDental';
import { HistoryTable } from './HistoryTable';
import type { Database } from '@/types/database.generated';
import { Button } from '@/components/ui/Button';

type TreatmentRow = Database['public']['Tables']['tooth_treatments']['Row'];

// ── Props ─────────────────────────────────────────────────────────────────────

interface HistoryPanelProps {
  patientId: string;
  clinicId: string;
  className?: string;
}

// ── CSV Export ────────────────────────────────────────────────────────────────

function downloadCsv(rows: TreatmentRow[]) {
  const header = ['Dato', 'Tand', 'Kode', 'Beskrivelse', 'Behandler', 'Klinik', 'Pris'];
  const csvRows = rows.map((r) => [
    r.treatment_date,
    r.tooth_fdi,
    r.treatment_type,
    `"${r.treatment_description.replace(/"/g, '""')}"`,
    r.performed_by,
    r.performed_at_other_practice
      ? r.other_practice_name
        ? `Anden klinik - ${r.other_practice_name}`
        : 'Anden klinik'
      : 'Egen',
    r.actual_cost ?? r.estimated_cost ?? '',
  ]);

  const content = [header, ...csvRows].map((row) => row.join(';')).join('\n');
  const blob = new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `behandlingshistorik-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Component ─────────────────────────────────────────────────────────────────

export function HistoryPanel({ patientId, className }: HistoryPanelProps) {
  const { t } = useTranslation();

  // Realtime subscription hoisted to DentalModulePage (single channel per mount).

  const [filters, setFilters] = useState<TreatmentHistoryFilters>({});
  const [freeText, setFreeText] = useState('');

  const activeFilters: TreatmentHistoryFilters = useMemo(
    () => ({ ...filters, freeText: freeText || null }),
    [filters, freeText],
  );

  const { data: allRows = [], isLoading } = usePatientTreatmentHistoryFiltered(
    patientId,
    activeFilters,
  );

  // Derive FDI list for filter dropdown
  const { data: allHistoryUnfiltered = [] } = usePatientTreatmentHistoryFiltered(patientId, {});
  const availableFdis = useMemo(() => {
    const s = new Set(allHistoryUnfiltered.map((r) => r.tooth_fdi));
    return [...s].sort();
  }, [allHistoryUnfiltered]);

  const setFilter = <K extends keyof TreatmentHistoryFilters>(
    key: K,
    value: TreatmentHistoryFilters[K],
  ) => setFilters((prev) => ({ ...prev, [key]: value }));

  const clearFilters = () => {
    setFilters({});
    setFreeText('');
  };

  const hasFilters =
    Object.values(filters).some((v) => v != null && v !== false) || Boolean(freeText);

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-ds-line bg-ds-card flex-shrink-0 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-ds-text">
          {t('dental.history.title')}
          {allRows.length > 0 && (
            <span className="ml-2 text-xs font-normal text-ds-muted">
              {allRows.length} poster
            </span>
          )}
        </h3>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => downloadCsv(allRows)}
            disabled={allRows.length === 0}
          >
            <Download />
            {t('dental.history.exportCsv')}
          </Button>
          <Button type="button" variant="secondary" onClick={() => window.print()}>
            <FileText />
            {t('dental.history.exportPdf')}
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-ds-sunk border-b border-ds-line flex-shrink-0">
        {/* Free-text */}
        <input
          type="text"
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          placeholder="Søg..."
          className="rounded-lg border border-ds-line bg-ds-card px-2.5 py-1.5 text-xs text-ds-text focus:outline-none focus:ring-2 focus:ring-ds-teal w-36"
        />

        {/* Date from */}
        <input
          type="date"
          value={filters.dateFrom ?? ''}
          onChange={(e) => setFilter('dateFrom', e.target.value || null)}
          title={t('dental.history.filters.dateRange')}
          className="rounded-lg border border-ds-line bg-ds-card px-2.5 py-1.5 text-xs text-ds-text focus:outline-none focus:ring-2 focus:ring-ds-teal"
        />
        <span className="text-xs text-ds-muted">–</span>
        <input
          type="date"
          value={filters.dateTo ?? ''}
          onChange={(e) => setFilter('dateTo', e.target.value || null)}
          className="rounded-lg border border-ds-line bg-ds-card px-2.5 py-1.5 text-xs text-ds-text focus:outline-none focus:ring-2 focus:ring-ds-teal"
        />

        {/* Tooth filter */}
        <select
          value={filters.toothFdi ?? ''}
          onChange={(e) => setFilter('toothFdi', e.target.value || null)}
          className="rounded-lg border border-ds-line bg-ds-card px-2.5 py-1.5 text-xs text-ds-text focus:outline-none focus:ring-2 focus:ring-ds-teal"
        >
          <option value="">{t('dental.history.filters.tooth')}</option>
          {availableFdis.map((fdi) => (
            <option key={fdi} value={fdi}>
              {fdi}
            </option>
          ))}
        </select>

        {/* Klinik toggle */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() =>
              setFilter('onlyOtherPractice', filters.onlyOtherPractice ? undefined : true)
            }
            className={cn(
              'rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors',
              filters.onlyOtherPractice
                ? 'border-ds-field bg-ds-sunk text-ds-text'
                : 'border-ds-line bg-ds-card text-ds-muted hover:bg-ds-sunk',
            )}
          >
            {t('dental.history.filters.otherClinic')}
          </button>
          <button
            type="button"
            onClick={() =>
              setFilter('onlyOwnPractice', filters.onlyOwnPractice ? undefined : true)
            }
            className={cn(
              'rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors',
              filters.onlyOwnPractice
                ? 'border-ds-teal bg-ds-teal-tint text-ds-teal bg-ds-teal-tint text-ds-teal'
                : 'border-ds-line bg-ds-card text-ds-muted hover:bg-ds-sunk',
            )}
          >
            {t('dental.history.filters.ownClinic')}
          </button>
        </div>

        {hasFilters && (
          <Button type="button" variant="link" onClick={clearFilters}>
            Ryd filtre
          </Button>
        )}
      </div>

      {/* Table area */}
      <div className="flex-1 overflow-auto bg-ds-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-ds-teal" />
          </div>
        ) : (
          <HistoryTable rows={allRows} isLoading={isLoading} />
        )}
      </div>
    </div>
  );
}
